import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaChevronDown, FaCopy, FaDownload, FaExclamationCircle } from "react-icons/fa";
import { Modal } from "../../../work/WorkUi";
import { useNotify } from "../../../../components/ConfirmModal";
import { errorMessage } from "../../dmApi";
import SetupCategory from "./SetupCategory";
import SetupConnections from "./SetupConnections";
import SetupGroupsPanel from "./SetupGroupsPanel";
import SetupProgressHeader from "./SetupProgressHeader";
import SetupStepDrawer from "./SetupStepDrawer";
import { NOT_APPLICABLE, SetupStatus, copyText, fmtShort, isDone, isOpen } from "./setupUi";

// Right-panel entry that shows only the API Connections section.
const CONNECTIONS_GROUP = "connections";

const FILTERS = [
  { key: "all", label: "All steps" },
  { key: "open", label: "Pending" },
  { key: "client", label: "Client to do" },
  { key: "agency", label: "Cloud Office to do" },
  { key: "review", label: "Submitted" },
  { key: "done", label: "Done" },
];

const CLIENT_FILTERS = [
  { key: "client", label: "My actions" },
  { key: "agency", label: "Cloud Office" },
  { key: "done", label: "Done" },
  { key: "all", label: "All steps" },
];

const matches = (step, filter, hideNa) => {
  if (hideNa && step.Status === NOT_APPLICABLE) return false;
  switch (filter) {
    case "open":
      return isOpen(step);
    case "client":
      return isOpen(step) && step.Owner === "Client";
    case "agency":
      return isOpen(step) && step.Owner === "Agency";
    case "review":
      return step.Status === "Submitted";
    case "done":
      return isDone(step.Status);
    default:
      return true;
  }
};

function ExportModal({ api, tenantId, name, onClose }) {
  const notify = useNotify();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.exportSetup(tenantId).then(setSummary).catch((err) => setError(errorMessage(err, "Could not build the summary")));
  }, [api, tenantId]);

  const download = () => {
    const blob = new Blob([summary.Text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${String(name || "client").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-dm-setup.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      title="Setup summary"
      onClose={onClose}
      wide
      footer={
        summary ? (
          <>
            <button type="button" className="dmp-btn dmp-btn--ghost" onClick={download}>
              <FaDownload aria-hidden="true" /> Download .txt
            </button>
            <button
              type="button"
              className="dmp-btn"
              onClick={async () => (await copyText(summary.Text)) && notify.success("Summary copied")}
            >
              <FaCopy aria-hidden="true" /> Copy
            </button>
          </>
        ) : null
      }
    >
      <p className="dmp-muted">IDs, links and statuses to share with the client or a developer. Passwords and files are never included.</p>
      {error ? <div className="dmp-error">{error}</div> : null}
      {summary ? <pre className="dms-export">{summary.Text}</pre> : !error ? <p className="dmp-muted">Preparing…</p> : null}
    </Modal>
  );
}

function RemindModal({ api, tenantId, pending, onClose, onSent }) {
  const notify = useNotify();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const open = pending.filter((s) => s.Status !== "Submitted");

  const send = async () => {
    setSending(true);
    setError("");
    try {
      const result = await api.remindSetup(tenantId, { Message: message.trim() || undefined });
      notify.success(`Reminder sent to ${result.Sent_To} (${result.Pending} items)`);
      onSent?.();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Could not send the reminder"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title="Remind client"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="dmp-btn" onClick={send} disabled={sending || !open.length}>
            {sending ? "Sending…" : "Send reminder"}
          </button>
        </>
      }
    >
      <p>The client owner gets an in-app alert, an email and (when enabled) a WhatsApp message listing these items:</p>
      <ul className="dms-remind-list">
        {open.map((s) => (
          <li key={s.Tenant_Step_Id}>
            {s.Title} <span className="dmp-muted">· {s.Category}</span>
            {s.Due_Date ? <span className="dmp-muted"> · due {fmtShort(s.Due_Date)}</span> : null}
          </li>
        ))}
      </ul>
      <label className="dmp-field dmp-field--full">
        <span>Personal note (optional)</span>
        <textarea rows={3} value={message} maxLength={500} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. Please share the GMB postcard code when it arrives." />
      </label>
      {error ? <div className="dmp-error">{error}</div> : null}
    </Modal>
  );
}

/** Account creation and integration checklist for one DM client (staff) or the signed-in company (portal). */
function SetupTab({ api, tenantId = null, name, onProgress, onEditTemplate }) {
  const notify = useNotify();
  const isStaff = Boolean(api.isStaff);
  const [setup, setSetup] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [openCats, setOpenCats] = useState(null);
  const [filter, setFilter] = useState(isStaff ? "all" : "client");
  const [group, setGroup] = useState("all");
  const [panelOpen, setPanelOpen] = useState(null);
  const [connectionsOpen, setConnectionsOpen] = useState(false);
  const [mineOpen, setMineOpen] = useState(true);
  const [hideNa, setHideNa] = useState(false);
  const [stepId, setStepId] = useState(null);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState("");
  const listRef = useRef(null);
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;

  const load = useCallback(
    () =>
      api
        .setup(tenantId)
        .then((data) => {
          setSetup(data);
          setError("");
          onProgressRef.current?.(data.Progress?.Pct ?? null);
          const first = data.Categories[0]?.Category_Id;
          const initialOpen = isStaff ? (first ? [first] : []) : data.Categories.map((c) => c.Category_Id);
          setOpenCats((prev) => prev || new Set(initialOpen));
          setPanelOpen((prev) => prev ?? first ?? null);
        })
        .catch((err) => setError(errorMessage(err, "Could not load the setup checklist"))),
    [api, tenantId, isStaff]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isStaff) return;
    api
      .meta()
      .then((m) => setUsers(m?.users || []))
      .catch(() => {});
  }, [api, isStaff]);

  const filtered = useMemo(() => {
    if (!setup) return [];
    return setup.Categories.filter((c) => group === "all" || c.Category_Id === group).map((c) => ({
      ...c,
      visible: c.Steps.filter((s) => matches(s, filter, hideNa)),
    }));
  }, [setup, filter, hideNa, group]);

  if (error && !setup) return <div className="dmp-error">{error}</div>;
  if (!setup) return <div className="dmp-card dmp-empty">Loading setup checklist…</div>;

  const toggleCat = (id) =>
    setOpenCats((prev) => {
      const next = new Set(prev || []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const runChecks = async () => {
    setBusy("checks");
    try {
      const result = await api.verifyAllSetup(tenantId);
      notify.success(`Checks finished: ${result.Passed} of ${result.Checked} passed`);
      await load();
    } catch (err) {
      notify.error(errorMessage(err, "Could not run the checks"));
    } finally {
      setBusy("");
    }
  };

  const clientPending = setup.Client_Pending || [];
  const filtersActive = filter !== "all" || hideNa;
  const shownCats = filtered.filter((c) => !filtersActive || c.visible.length);
  const connectionsOnly = isStaff && group === CONNECTIONS_GROUP;
  const showConnections = isStaff && (group === "all" || connectionsOnly);
  const connectionsSection = (
    <SetupConnections
      isStaff={isStaff}
      tenantId={tenantId}
      open={connectionsOpen}
      collapsible={!connectionsOnly}
      onToggle={() => setConnectionsOpen((v) => !v)}
    />
  );
  const pickGroup = (id) => {
    setGroup(id);
    if (id !== "all") setPanelOpen(id);
    if (id !== "all" && id !== CONNECTIONS_GROUP) setOpenCats((prev) => new Set([...(prev || []), id]));
    setTimeout(() => listRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
  };

  return (
    <div className="dms dms-layout">
      <div className="dms-layout__main">
        {error ? <div className="dmp-error">{error}</div> : null}

        <SetupProgressHeader
          setup={setup}
          isStaff={isStaff}
          busy={busy}
          onRemind={() => setModal("remind")}
          onExport={() => setModal("export")}
          onRunChecks={runChecks}
          onEditTemplate={onEditTemplate}
        />

        {!isStaff && clientPending.length ? (
          <section className={`dmc-card dms-mine${mineOpen ? " is-open" : ""}`}>
            <h3 className="dmc-card__title">
              <button type="button" className="dms-mine__head" aria-expanded={mineOpen} onClick={() => setMineOpen((v) => !v)}>
                <FaExclamationCircle aria-hidden="true" /> Your pending items ({clientPending.length})
                <span className="dmp-section__toggle">
                  <FaChevronDown aria-hidden="true" />
                </span>
              </button>
            </h3>
            {mineOpen && clientPending.map((s) => (
              <button key={s.Tenant_Step_Id} type="button" className="dms-mine__item" onClick={() => setStepId(s.Tenant_Step_Id)}>
                <span className="dms-mine__name">
                  {s.Title}
                  <span className="dmp-muted">
                    {s.Category}
                    {s.Due_Date ? ` · due ${fmtShort(s.Due_Date)}` : ""}
                  </span>
                </span>
                <SetupStatus status={s.Status} small />
              </button>
            ))}
          </section>
        ) : null}
      </div>

      <aside className="dms-layout__side">
        <SetupGroupsPanel
          setup={setup}
          group={group}
          expanded={panelOpen}
          onPickGroup={pickGroup}
          showConnections={isStaff}
        />
      </aside>

      <div className="dms-layout__list" ref={listRef}>
        {connectionsOnly ? null : (
        <div className="dms-toolbar">
          <div className="dms-chips" role="group" aria-label="Filter steps">
            {(isStaff ? FILTERS : CLIENT_FILTERS).map((f) => (
              <button
                key={f.key}
                type="button"
                className={`dms-chip${filter === f.key ? " is-active" : ""}`}
                aria-pressed={filter === f.key}
                onClick={() => {
                  setFilter(f.key);
                  if (f.key !== "all") setOpenCats(new Set(setup.Categories.map((c) => c.Category_Id)));
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <label className="dms-check">
            <input type="checkbox" checked={hideNa} onChange={(e) => setHideNa(e.target.checked)} />
            <span>Hide not applicable</span>
          </label>
          {group === "all" ? (
            <button
              type="button"
              className="dmp-link-btn"
              onClick={() =>
                setOpenCats((prev) =>
                  prev && prev.size === setup.Categories.length ? new Set() : new Set(setup.Categories.map((c) => c.Category_Id))
                )
              }
            >
              {openCats && openCats.size === setup.Categories.length ? "Collapse all" : "Expand all"}
            </button>
          ) : null}
        </div>
        )}

        <div className="dms-cats">
          {shownCats.map((c) => (
            <React.Fragment key={c.Category_Id}>
              <SetupCategory
                category={c}
                steps={c.visible}
                open={Boolean(openCats?.has(c.Category_Id))}
                selected={panelOpen === c.Category_Id}
                collapsible={group === "all"}
                onToggle={() => toggleCat(c.Category_Id)}
                onOpenStep={setStepId}
                isStaff={isStaff}
              />
            </React.Fragment>
          ))}
          {showConnections ? connectionsSection : null}
          {!connectionsOnly && filtersActive && filtered.every((c) => !c.visible.length) ? (
            <div className="dmp-card dmp-empty">
              {!isStaff && filter === "client"
                ? "Nothing needs your input right now. Cloud Office is working on the rest."
                : "No steps match this filter."}
            </div>
          ) : null}
        </div>
      </div>

      {stepId ? (
        <SetupStepDrawer
          api={api}
          stepId={stepId}
          tenantId={tenantId}
          users={users}
          onClose={() => setStepId(null)}
          onChanged={load}
        />
      ) : null}
      {modal === "export" ? <ExportModal api={api} tenantId={tenantId} name={name || setup.Tenant?.Name} onClose={() => setModal(null)} /> : null}
      {modal === "remind" ? (
        <RemindModal api={api} tenantId={tenantId} pending={clientPending} onClose={() => setModal(null)} onSent={load} />
      ) : null}
    </div>
  );
}

export default SetupTab;
